import { create } from "zustand";

interface Task {
  id: string;
  product: { name: string; retailer: string; url: string; imageUrl?: string };
  profile: { name: string };
  quantity: number;
  status: string;
  enabled: boolean;
  successCount: number;
  failCount: number;
  queuePosition?: number;
  queueEstimatedWait?: number;
}

interface Stats {
  products: number;
  profiles: number;
  tasks: { total: number; active: number };
  proxies: number;
  orders: number;
}

interface AppState {
  // Connection status
  connected: boolean;
  setConnected: (connected: boolean) => void;

  // Tasks
  tasks: Task[];
  setTasks: (tasks: Task[]) => void;
  updateTask: (id: string, updates: Partial<Task>) => void;

  // Stats
  stats: Stats | null;
  setStats: (stats: Stats) => void;

  // WebSocket
  ws: WebSocket | null;
  connectWebSocket: () => void;
  disconnectWebSocket: () => void;

  // Notifications
  notifications: Array<{ id: string; type: string; title: string; message: string; timestamp: Date }>;
  addNotification: (notification: Omit<AppState["notifications"][0], "id" | "timestamp">) => void;
  removeNotification: (id: string) => void;
}

export const useStore = create<AppState>((set, get) => ({
  connected: false,
  setConnected: (connected) => set({ connected }),

  tasks: [],
  setTasks: (tasks) => set({ tasks }),
  updateTask: (id, updates) =>
    set((state) => ({
      tasks: state.tasks.map((t) => (t.id === id ? { ...t, ...updates } : t)),
    })),

  stats: null,
  setStats: (stats) => set({ stats }),

  ws: null,
  connectWebSocket: () => {
    const wsUrl = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/ws/events`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log("WebSocket connected");
      set({ connected: true });
    };

    ws.onclose = () => {
      console.log("WebSocket disconnected");
      set({ connected: false, ws: null });
      // Reconnect after 3 seconds
      setTimeout(() => get().connectWebSocket(), 3000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data, get, set);
      } catch (e) {
        console.error("Failed to parse WebSocket message", e);
      }
    };

    set({ ws });
  },
  disconnectWebSocket: () => {
    const { ws } = get();
    if (ws) {
      ws.close();
      set({ ws: null });
    }
  },

  notifications: [],
  addNotification: (notification) =>
    set((state) => ({
      notifications: [
        ...state.notifications,
        { ...notification, id: Math.random().toString(36).slice(2), timestamp: new Date() },
      ].slice(-10), // Keep last 10
    })),
  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),
}));

function handleWebSocketMessage(
  data: { type: string; data: unknown },
  get: () => AppState,
  _set: (state: Partial<AppState> | ((state: AppState) => Partial<AppState>)) => void
) {
  switch (data.type) {
    case "stock:change":
      const stockData = data.data as { productId: string; currentStatus: string };
      get().addNotification({
        type: stockData.currentStatus === "in_stock" ? "success" : "info",
        title: "Stock Change",
        message: `Product ${stockData.productId} is now ${stockData.currentStatus}`,
      });
      break;

    case "checkout:success":
      const successData = data.data as { orderId?: string };
      get().addNotification({
        type: "success",
        title: "Checkout Success!",
        message: `Order placed: ${successData.orderId || "N/A"}`,
      });
      break;

    case "checkout:failed":
      const failData = data.data as { error?: string };
      get().addNotification({
        type: "error",
        title: "Checkout Failed",
        message: failData.error || "Unknown error",
      });
      break;

    case "monitor:check":
      // Silent update - could update task status
      break;
  }
}

